import { AuthorizableLAANCAdvisoryProperties, ICoordinate, ILAANCStatus, ISO8601DateTimeString } from '@autopylot-internal/tiles-client';
import { IOperator } from '../../profile/IOperator.ts';
import { formatLocalDateAndTime, formatPhone } from '../../support/helpers/format.ts';

// NOTE: This entire class is duplicated in autopylot-api!!!

export class LAANCAuthorizationTextProvider {
    private licensed = `
    [LAANC Reference Code / FAA Facility Code, Start Date & Time – End Date & Time, Max Alt
    num ft]: In accordance with Title 14 CFR Part 107.41, your operation is authorized within the
    designated airspace and timeframe constraints. Altitude limits are absolute values above ground
    level which shall not be added to the height of any structures. This Authorization is subject to
    cancellation at any time upon notice by the FAA Administrator or his/her authorized
    representative. This Authorization does not constitute a waiver of any State law or local
    ordinance. [Name of operator] is the person designated as responsible for the overall safety of
    UAS operations under this Authorization. During UAS operations for on-site
    communication/recall, [name of operator] shall be continuously available for direct contact at
    [contact phone number] by Air Traffic. Remote pilots are responsible to check the airspace they
    are operating in and comply with all restrictions that may be present in accordance with 14 CFR
    107.45 and 107.49 (a)(2), such as restricted and Prohibited Airspace, Temporary Flight
    Restrictions, etc. Remote pilots are also responsible for complying with the operating
    requirements in 14 CFR 107.29(a) when operating at night. Operations are not authorized in
    Class E airspace when there is a weather ceiling less than 1,000 feet AGL. If the UAS loses
    communications or loses its GPS signal, it must return to a predetermined location within the
    operating area and land. The remote pilot in command must abort the flight in the event of
    unpredicted obstacles or emergencies. This certificate shall be presented for inspection upon the
    request of any authorized representative of the Federal Aviation Administration, or of any
    Federal, State, or municipal official charged with the duty of enforcing local laws or
    regulations.`;

    private recreational = `
    [LAANC Reference Code / FAA Facility Code, Start Date & Time – End Date & Time, Max Alt
    num ft]: In accordance with 49 U.S.C. § 44809(a)(5), your operation is authorized within the
    designated airspace and timeframe constraints. Altitude limits are absolute values above ground
    level which shall not be added to the height of any structures. This Authorization is subject to
    cancellation at any time upon notice by the FAA Administrator or his/her authorized
    representative. This Authorization does not constitute a waiver of any State law or local
    ordinance. [Name of operator] is the person designated as responsible for the overall safety of
    UAS operations under this Authorization. During UAS operations for on-site
    communication/recall, [name of operator] shall be continuously available for direct contact at
    [contact phone number] by Air Traffic. [Name of operator] is responsible to check the airspace
    in which the UAS will be operated and comply with all restrictions that may be present in
    accordance with § 44809(a)(5), such as restricted and prohibited airspace, night operations,
    temporary flight restrictions, etc. This authorization is subject to the following conditions: (1)
    operations are not authorized in Class E surface area airspace when there is a weather ceiling less
    than 1,000 feet AGL; (2) if the UAS loses communications or loses its GPS signal, it must return
    to a predetermined location within the operating area and land; (3) night operations are only
    permitted if the operator has completed FAA training/testing and lighted their sUAS with anti-
    collision lighting visible for at least 3 statute miles that has a flash rate sufficient to avoid a
    collision. and (4) the person manipulating the controls of the UAS must abort the flight in the
    event of unpredicted obstacles or emergencies. This certificate shall be presented for inspection
    upon the request of any authorized representative of the Federal Aviation Administration, or of
    any Federal, State, or municipal official charged with the duty of enforcing local laws or
    regulations.`;

    // convert entire paragraph to a single line to avoid formatting problems in apple mail
    private toSingleLine = (text: string) => {
        const lines = text.trim().split('\n').map(line => {
            const trimmed = line.trim();
            const hyphenated = (trimmed.endsWith('-') && !trimmed.endsWith(' -'));
            return hyphenated ? trimmed : trimmed + ' ';
        });
        return lines.join('');
    };

    private interpolateHTMLAuthorization = ({ rawText, operator, authorization, altitude, location  }: {rawText: string; operator: IOperator; authorization: AuthorizableLAANCAdvisoryProperties; altitude: number; location: ICoordinate; }) => {
        const getFormattedTime = (time: string) => formatLocalDateAndTime({ location, time, withTimeZone: true });
        // warning: altitude being passed in, is coming from the mission and ideally would instead would come from the authorization
        // which will be necessary when we support changing authorizations
        // eg I have an authorization for 400 even though my mission is now 300, b/c it isn't
        // worth changing the authorization I already had approved at 400

        const operatorNameHtml = `<b><span style={{whiteSpace: 'nowrap'}}>${operator.firstName} ${operator.lastName}</span></b>`;
        const phoneNumberHtml = `<b><span style={{whiteSpace: 'nowrap'}}>${formatPhone(operator.phone!)}</span></b>`;

        return this.toSingleLine(rawText)
            .replace(
                '[LAANC Reference Code / FAA Facility Code, Start Date & Time – End Date & Time, Max Alt num ft]',
                `<b>APY${authorization.operationID} / ${authorization.airspaceIdent}, <span style={{whiteSpace: 'nowrap'}}>${getFormattedTime(authorization.startTime)}</span> – <span style={{whiteSpace: 'nowrap'}}>${getFormattedTime(authorization.endTime)}</span>, Max Alt ${altitude}&nbsp;ft</b>`
            )
            .split('[Name of operator]').join(operatorNameHtml)
            .split('[name of operator]').join(operatorNameHtml)
            .split('1,000 feet').join('1,000&nbsp;feet')
            .split('[contact phone number]').join(phoneNumberHtml);
    };

    private interpolateTextAuthorization = ({ rawText, operator, authorization, altitude, location  }: {rawText: string; operator: IOperator; authorization: AuthorizableLAANCAdvisoryProperties; altitude: number; location: ICoordinate; }) => {
        const getFormattedTime = (time: string) => formatLocalDateAndTime({ location, time, withTimeZone: true });

        const operatorName = `${operator.firstName} ${operator.lastName}`;
        const phoneNumber = `${formatPhone(operator.phone!)}`;

        return this.toSingleLine(rawText)
            .replace(
                '[LAANC Reference Code / FAA Facility Code, Start Date & Time – End Date & Time, Max Alt num ft]',
                `APY${authorization.operationID} / ${authorization.airspaceIdent}, ${getFormattedTime(authorization.startTime)} – ${getFormattedTime(authorization.endTime)}, Max Alt ${altitude}ft`
            )
            .split('[Name of operator]').join(operatorName)
            .split('[name of operator]').join(operatorName)
            .split('[contact phone number]').join(phoneNumber);
    };

    getAuthorizationAsHTML = ({ operator, authorization, altitude, location }: { operator: IOperator; authorization: AuthorizableLAANCAdvisoryProperties; location: ICoordinate; altitude: number; }): string => {
        const rawText = authorization.isLicensed ? this.licensed : this.recreational;
        return this.interpolateHTMLAuthorization({
            rawText,
            altitude,
            authorization,
            location,
            operator
        });
    };

    getAuthorizationAsText = (params: { operator: IOperator; authorization: AuthorizableLAANCAdvisoryProperties; location: ICoordinate; altitude: number; }): string => {
        const rawText = params.authorization.isLicensed ? this.licensed : this.recreational;
        return this.interpolateTextAuthorization({
            rawText,
            ...params
        });
    };

    getAuthorizationSummary = ({ authorization, endTime }: { authorization: AuthorizableLAANCAdvisoryProperties; endTime: ISO8601DateTimeString }) => {
        const { operationID, airspaceIdent } = authorization;
        const statusLabel = this.getAuthorizationStatusLabel({ authorization, endTime });
        return `${statusLabel}: APY${operationID} (${airspaceIdent})`;
    };

    getAuthorizationStatusLabel = ({ authorization, endTime }: { authorization: AuthorizableLAANCAdvisoryProperties; endTime: ISO8601DateTimeString }) => {
        const { getAPLAANCStatus } = authorization;
        const status = getAPLAANCStatus({ endTime });
        return laancStatusLabels[status];
    };
}

const laancStatusLabels: { [status in ILAANCStatus]: string } = {
    'initialized': 'Pending',
    'authorizing': 'Pending',
    'authorizing-failed': 'Not Valid',
    'authorized': 'Approved',
    'rescinded': 'Not Valid',
    'rescind-acked': 'Canceled per FAA',
    'invalidated': 'Not Valid',
    'invalidated-acked': 'Canceled per FAA',
    'never-authorized': 'Canceled',
    'user-canceled': 'Canceled',
    'user-closed': 'Closed',
    'user-deleted': 'Deleted'
};
